import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useUser } from "../../Data/UserContext";
import { consumeAccountDeletedIfPresent, getAuthHeaders } from "../../utils/authHeaders";
import { resolveAvatarUrl } from "../../utils/sessionUser";
import { getProfileCountryFlagEmoji } from "../../utils/profileCountryFlag";
import {
  formatXpHistoryReasonDetail,
  formatXpHistoryRelative,
  getXpWeekStart,
  parseXpSummary,
  type XpSummary,
} from "../../utils/xp";
import { ProfileModalShell } from "../Profile/ProfileModalShell";
import { useCloseOnHistoryBack } from "../../hooks/useCloseOnHistoryBack";
import { ProfileAvatar } from "../Profile/ProfileAvatar";
import { ModalCloseButton } from "../ModalCloseIcon";
import { XpCareerStatsRow, XP_STAT_ICON_CLASS, XP_STAT_VALUE_CLASS } from "./XpCareerStatsRow";
import { XpHistoryListRow, type XpHistoryListItem } from "./XpHistoryListRow";
import { XpAboutModal } from "./XpAboutModal";
import { XP_BACK_ARROW_CLASS, XP_BACK_ARROW_SRC, XP_LIME_BACK_BUTTON_CLASS } from "./xpModalNav";

type XpHistoryRow = {
  amount: number;
  reason: string | null;
  created_at: string;
};

type HistoryPeriod = "week" | "all";

type XpHubModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** When omitted, fetched on open. */
  xp?: XpSummary | null;
  /** Lime back-button label. Defaults to PROFILE (profile entry). */
  backLabel?: string;
};

function apiRowToListItem(
  row: XpHistoryRow,
  avatarSrc: string | null,
  displayName: string,
  frame: { avatarFrameUrl: string | null; avatarFrameSmallUrl: string | null },
): XpHistoryListItem {
  return {
    amount: row.amount,
    detail: formatXpHistoryReasonDetail(row.reason),
    relative: formatXpHistoryRelative(row.created_at),
    iconSrc: avatarSrc,
    displayName,
    ...frame,
  };
}

export function XpHubModal({ isOpen, onClose, xp: xpProp, backLabel = "PROFILE" }: XpHubModalProps) {
  useCloseOnHistoryBack(isOpen, onClose);
  const navigate = useNavigate();
  const { user } = useUser();
  const [xp, setXp] = useState<XpSummary | null>(xpProp ?? null);
  const [rows, setRows] = useState<XpHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [historyPeriod, setHistoryPeriod] = useState<HistoryPeriod>("week");
  const [aboutOpen, setAboutOpen] = useState(false);

  const displayName = (user?.playerName || user?.username || "Player").trim();
  const countryFlag = useMemo(() => getProfileCountryFlagEmoji(user?.country), [user?.country]);
  const avatarSrc = resolveAvatarUrl(user) || null;
  const avatarFrameUrl = user?.avatar_frame_url ?? null;
  const avatarFrameSmallUrl = user?.avatar_frame_small_url ?? null;

  const fetchData = useCallback(async () => {
    if (!user?.userId || !process.env.API_URL) return;
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [summaryRes, historyRes] = await Promise.all([
        xpProp
          ? Promise.resolve(null)
          : fetch(`${process.env.API_URL}/account/xp-summary`, {
              method: "GET",
              headers,
            }),
        fetch(`${process.env.API_URL}/account/xp-history?limit=50`, {
          method: "GET",
          headers,
        }),
      ]);
      if (!xpProp && summaryRes) {
        if (await consumeAccountDeletedIfPresent(summaryRes)) return;
        if (summaryRes.ok) {
          const summaryData = await summaryRes.json();
          setXp(parseXpSummary(summaryData));
        }
      }
      if (historyRes) {
        if (await consumeAccountDeletedIfPresent(historyRes)) return;
        if (historyRes.ok) {
          const data = await historyRes.json();
          setRows(Array.isArray(data.history) ? data.history : []);
        } else {
          setRows([]);
        }
      }
    } catch {
      if (!xpProp) setXp(null);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user?.userId, xpProp]);

  useEffect(() => {
    if (xpProp) setXp(xpProp);
  }, [xpProp]);

  useEffect(() => {
    if (isOpen) void fetchData();
  }, [isOpen, fetchData]);

  useEffect(() => {
    if (!isOpen) return;
    const onXpActivity = () => {
      void fetchData();
    };
    window.addEventListener("c8-notifications-updated", onXpActivity);
    return () => window.removeEventListener("c8-notifications-updated", onXpActivity);
  }, [isOpen, fetchData]);

  useEffect(() => {
    if (!isOpen) setAboutOpen(false);
  }, [isOpen]);

  const weekStart = useMemo(() => getXpWeekStart(), [isOpen, historyPeriod]);

  const filteredRows = useMemo(() => {
    if (historyPeriod === "all") return rows;
    return rows.filter((row) => {
      if (!row.created_at) return false;
      const t = new Date(row.created_at).getTime();
      return !Number.isNaN(t) && t >= weekStart.getTime();
    });
  }, [rows, historyPeriod, weekStart]);

  const historyListItems = useMemo(
    () =>
      filteredRows.map((row) =>
        apiRowToListItem(row, avatarSrc, displayName, { avatarFrameUrl, avatarFrameSmallUrl })
      ),
    [filteredRows, avatarSrc, displayName, avatarFrameUrl, avatarFrameSmallUrl]
  );

  const historyEmptyMessage =
    historyPeriod === "week" ? "No XP earned this week yet." : "No XP history yet.";

  const openLeaderboardPage = () => {
    onClose();
    navigate("/xp-leaderboard");
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen ? (
        <ProfileModalShell
          onClose={onClose}
          zIndexClass="z-[10060]"
          overlayAriaLabel="Close XP"
        >
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="shrink-0 border-b border-white/10 px-3 pb-1 pt-1 sm:pt-1.5">
              <div className="grid shrink-0 grid-cols-[1fr_auto_1fr] items-start gap-x-2">
                <div className="col-start-1 flex min-w-0 flex-col items-start self-start">
                  <button type="button" onClick={onClose} className={XP_LIME_BACK_BUTTON_CLASS}>
                    <img src={XP_BACK_ARROW_SRC} alt="" className={XP_BACK_ARROW_CLASS} aria-hidden />
                    {backLabel}
                  </button>
                </div>
                <div className="col-start-2 flex max-w-[min(100%,11rem)] flex-col items-center justify-self-center self-start">
                  <div className="relative h-[70px] w-[70px] shrink-0">
                    <ProfileAvatar
                      variant="hero"
                      src={avatarSrc ?? ""}
                      displayName={displayName}
                      alt=""
                      xpWeekCrown={user?.xp_week_crown}
                      avatarFrameUrl={avatarFrameUrl}
                      avatarFrameSmallUrl={avatarFrameSmallUrl}
                      className="!h-[70px] !w-[70px] shadow-[0_6px_20px_rgba(8,2,18,0.45)]"
                    />
                    {countryFlag ? (
                      <span
                        className="pointer-events-none absolute -right-1.5 -top-1.5 z-[1] select-none text-[15px] leading-none"
                        aria-hidden
                      >
                        {countryFlag}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="col-start-3 justify-self-end self-start">
                  <ModalCloseButton
                    onClick={onClose}
                    ariaLabel="Close XP"
                    className="h-11 w-11 shrink-0 rounded-none hover:bg-white/10 hover:opacity-100"
                  />
                </div>
              </div>
              <p className="mt-2 truncate text-center font-anton text-[20px] font-normal uppercase leading-[26px] tracking-normal text-white [leading-trim:none]">
                {displayName}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4 pt-2">
              <div className="overflow-hidden border border-white/10">
                <XpCareerStatsRow
                  xp={xp}
                  statIconClassName={XP_STAT_ICON_CLASS}
                  statValueClassName={XP_STAT_VALUE_CLASS}
                  onLeaderboardClick={openLeaderboardPage}
                />
              </div>

              <div className="mt-4 py-2">
                <div className="mb-2 flex w-full items-center justify-between gap-2">
                  <h2 className="shrink-0 font-anton text-[15px] font-normal not-italic uppercase leading-[12px] tracking-[0%] text-white [leading-trim:none]">
                    XP history
                  </h2>
                  <div className="flex shrink-0 items-center gap-2">
                    <div className="box-border flex h-[30px] w-[130px] shrink-0 border border-solid border-[#1B062F]">
                      <select
                        value={historyPeriod}
                        onChange={(e) => setHistoryPeriod(e.target.value as HistoryPeriod)}
                        className="h-full min-w-0 flex-1 appearance-none rounded-none border-0 bg-white py-0 pl-2 pr-2 font-chakra text-[11px] font-normal normal-case leading-[16px] text-[#1B062F] [leading-trim:none]"
                        aria-label="XP history period"
                      >
                        <option value="week">This week</option>
                        <option value="all">All time</option>
                      </select>
                      <span
                        className="flex h-full w-[30px] shrink-0 items-center justify-center border-0 border-l border-solid border-[#1B062F] bg-white"
                        aria-hidden
                      >
                        <img
                          src="/assets/chevron-down.svg"
                          alt=""
                          className="h-3 w-3 object-contain"
                        />
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAboutOpen(true)}
                      className="inline-flex h-[30px] w-[60px] shrink-0 items-center justify-center rounded-none border-0 bg-white/15 p-0 text-center font-anton text-[12px] font-normal not-italic uppercase leading-[20px] tracking-[0%] text-white hover:bg-white/20 [leading-trim:none]"
                    >
                      About XP
                    </button>
                  </div>
                </div>

                {loading ? (
                  <p className="py-6 text-center font-chakra text-[12px] text-white/50">Loading…</p>
                ) : historyListItems.length === 0 ? (
                  <p className="py-6 text-center font-chakra text-[12px] text-white/50">{historyEmptyMessage}</p>
                ) : (
                  <ul className="flex flex-col gap-1.5">
                    {historyListItems.map((item, i) => (
                      <XpHistoryListRow key={`${item.relative}-${item.detail}-${i}`} item={item} />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </ProfileModalShell>
      ) : null}
      <XpAboutModal isOpen={aboutOpen} onClose={() => setAboutOpen(false)} />
    </AnimatePresence>,
    document.body
  );
}
